import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { Feature, FeatureCollection, Point } from 'geojson';
import type { MapRef } from 'react-map-gl/maplibre';

import type { PoiFeatureProperties } from '@/components/map/utils/poiFeatureProperties';
import { normaliseEntityType } from '@/components/map/utils/entityStatus';
import { fetchEntitiesForWorklist } from '@/api/poisApi';
import { POI_MIN_ZOOM } from '@/hooks/useEntitiesLayer';
import { toLngLatTuple } from '@/utils/mapUtils';

export interface PoiNavigationState {
    navigateNext: () => void;
    navigatePrevious: () => void;
    /** True when at least 2 POIs exist in the selected POI's worklist. */
    canNavigate: boolean;
}

export function usePoiNavigation(
    mapRef: RefObject<MapRef | null>,
    selectedPoi: PoiFeatureProperties | null,
    selectPoi: (poi: PoiFeatureProperties | null, coords?: [number, number]) => void,
    clientId: string | null,
): PoiNavigationState {
    const [worklistFeatures, setWorklistFeatures] = useState<Feature<Point, PoiFeatureProperties>[]>([]);
    const worklistFeaturesRef = useRef(worklistFeatures);
    useEffect(() => {
        worklistFeaturesRef.current = worklistFeatures;
    }, [worklistFeatures]);

    // Re-fetch only when the selected POI belongs to a different group_id.
    // Navigating Next/Previous within the same worklist leaves group_id unchanged.
    const worklistKey = selectedPoi?.group_id ?? null;

    useEffect(() => {
        if (!worklistKey || !clientId) return;

        let cancelled = false;
        // Previous/Next steps through POI entities only — leak features are family
        // satellites reached from the details panel, not worklist stops.
        fetchEntitiesForWorklist(clientId, worklistKey)
            .then((fc) => ({
                ...fc,
                features: fc.features.filter(
                    (f) => normaliseEntityType((f.properties as PoiFeatureProperties | null)?.entity_type) === 'poi',
                ),
            }))
            .then((fc: FeatureCollection) => {
                if (!cancelled) {
                    setWorklistFeatures(fc.features as Feature<Point, PoiFeatureProperties>[]);
                }
            })
            .catch(() => {
                // Leave existing worklist features so navigation continues to work with
                // whatever data was available before the failed fetch.
            });

        return () => {
            cancelled = true;
            // Clear stale worklist data when the group changes or the selection is cleared.
            setWorklistFeatures([]);
        };
    }, [worklistKey, clientId]);

    const canNavigate = worklistFeatures.length > 1;

    const navigate = useCallback(
        (direction: 1 | -1) => {
            if (!selectedPoi) return;

            const features = [...worklistFeaturesRef.current];
            if (features.length <= 1) return;

            // Sort ascending by stop; push missing/non-numeric stops to the end.
            features.sort((a, b) => {
                const aStop = typeof a.properties.stop === 'number' ? a.properties.stop : Infinity;
                const bStop = typeof b.properties.stop === 'number' ? b.properties.stop : Infinity;
                return aStop - bStop;
            });

            const currentIndex = features.findIndex((f) => f.properties.id === selectedPoi.id);
            if (currentIndex === -1) return;

            const nextIndex = (currentIndex + direction + features.length) % features.length;
            const target = features[nextIndex];

            const coords = toLngLatTuple(target.geometry.coordinates);
            selectPoi(target.properties, coords);

            // Fly to target using the same validated coords passed to selectPoi.
            // Skipped when coords are undefined (non-Point or non-numeric geometry).
            const map = mapRef.current?.getMap();
            if (map && coords) {
                map.easeTo({
                    center: coords,
                    zoom: Math.max(map.getZoom(), POI_MIN_ZOOM),
                    duration: 600,
                });
            }
        },
        [selectedPoi, selectPoi, mapRef],
    );

    const navigateNext = useCallback(() => {
        navigate(1);
    }, [navigate]);
    const navigatePrevious = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    return { navigateNext, navigatePrevious, canNavigate };
}
